import React, { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { concat } from 'lodash-es';
import LiveTitleFilter from '@sg/pages/StudioManagement/LiveTitles/GPComponent/LiveTitleFilter';
import LivePlatFormFilter from '@sg/components/LivePlatFormFilter';
import LiveRegionFilter from '@sg/components/LiveRegionFilter';
import LiveMonthFilter from '@sg/components/LiveMonthFilter';
import { Button, Checkbox, Input, Justify, Radio } from 'tea-component';
import LiveMultipleSelectFilter from '@sg/pages/StudioManagement/LiveTitles/GPComponent/LiveMultipleSelectFilter';
import { FILE_TYPES_MAP } from '@gp/constants/const';
import SvgIcon from '@home/components/SvgIcon';
import GeneralModal from '@gp/components/GeneralModal';
import { UseFormSearch } from '@sg/components/UseFormSearch';
import https from '@sg/utils/httpC';
import { exportData } from '../../utils/exportExcelandCsv';
import './index.scss';
import {
  flattenMetrics,
  flattenMetricsBreakdown,
  flattenTimelineData,
  flattenTimelineDataBreakdown,
  METRIC_NAME_LIST,
  downloadColumnsFormData,
  generateExtendedColumns,
} from '../../utils';
import { useIntl } from 'umi';
import { createBtnPoint, SGBuriedBtnPoint, LiveTitlesTab } from '@/pages/strategic/utils/buriedPoint';

const Filter: React.FC<any> = (props: any) => {
  const { formatMessage } = useIntl();
  const {
    selectedDate,
    selectedTitle,
    selectedRegion,
    selectedPlatforms,
    platformGameTitle,
    selectYM,
    selectStudio,
    handleStudioChange,
    handleDateChange,
    handleTitleChange,
    handleComparisonChange,
    handleRegionChange,
    isAggregate,
    exportFetchParams,
  } = props;
  const [visible, setVisible] = useState(false);

  return (
    <>
      <Justify
        style={{
          marginBottom: '20px',
        }}
        top
        left={
          <section className="dbg-studio-monthly-report-download-title-card-filter">
            <LiveTitleFilter
              {...({
                value: concat([], selectedTitle),
                onChange: handleTitleChange,
                isSingle: false,
                boxClassName: 'dbs-metric-select-overlay',
              } as any)}
            />
            <LiveMultipleSelectFilter
              {...{
                value: selectStudio,
                onChange: handleStudioChange,
                boxClassName: 'dbs-metric-select-overlay',
              }}
            />
            <LiveMonthFilter
              {...({
                value: selectedDate,
                onChange: handleDateChange,
                overlayClassName: 'dbs-month-picker-overlay',
              } as any)}
            />
            <LivePlatFormFilter
              {...{
                gameTitle: platformGameTitle,
                allowGlobalFallback: true,
                value: selectedPlatforms,
                onChange: handleComparisonChange,
                boxClassName: 'dbs-metric-select-overlay',
              }}
            />
            <LiveRegionFilter
              {...{
                value: selectedRegion,
                onChange: handleRegionChange,
                boxClassName: 'dbs-metric-select-overlay dbc-region-select-container',
              }}
            />
            {visible && (
              <LiveFilterDownloadModal
                {...{
                  visible,
                  close: () => setVisible(false),
                  selectedDate,
                  selectedTitle,
                  isAggregate,
                  exportFetchParams,
                  selectYM,
                }}
              />
            )}
          </section>
        }
        right={
          <Button
            type="primary"
            className="dbs-gradient-btn"
            onClick={() => {
              // Track buried point for Download button
              const btnPoint = createBtnPoint(LiveTitlesTab, 'download', 'Download');
              if (btnPoint) {
                SGBuriedBtnPoint(btnPoint);
              }
              setVisible(true);
            }}
          >
            <SvgIcon type="download" style={{ position: 'relative', left: '-4px' }} />
            {formatMessage({ id: 'dbs_download' })}
          </Button>
        }
      />
    </>
  );
};

export default React.memo(Filter);

interface ILiveFilterDownloadModalProps {
  visible: boolean;
  close: () => void;
  selectedDate: any[];
  selectedTitle: string[];
  isAggregate: boolean;
  exportFetchParams: Record<string, any>;
  selectYM: string[];
}

export const LiveFilterDownloadModal = ({
  visible,
  close,
  selectedDate,
  selectedTitle,
  isAggregate,
  exportFetchParams,
  selectYM,
}: ILiveFilterDownloadModalProps) => {
  // Download dimension toggles - default all checked
  const [includePlatform, setIncludePlatform] = useState(true);
  const [includeRegion, setIncludeRegion] = useState(true);
  const [includeCountry, setIncludeCountry] = useState(true);
  // const [dimensionBreakdown, setDimensionBreakdown] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  // Reset to defaults when modal opens
  useEffect(() => {
    if (visible) {
      setIncludePlatform(true);
      setIncludeRegion(true);
      setIncludeCountry(true);
    }
  }, [visible]);

  const durationTitle = useMemo(() => {
    const isSingle = !selectedTitle?.length || selectedTitle.length > 1;
    const [start, end] = [moment(selectedDate[0]).format('YYYY-MM'), moment(selectedDate[1]).format('YYYY-MM')];
    return isSingle ? `Live Title all ${start} ~ ${end}` : `Live Title ${selectedTitle?.[0] || ''} ${start} ~ ${end}`;
  }, [selectedDate, selectedTitle]);

  const getFormValue = async (value: { fileFormat: string; duration: string }) => {
    const columns = downloadColumnsFormData(METRIC_NAME_LIST);
    const downloadOptions = { includePlatform, includeRegion, includeCountry };
    const excelColumns = generateExtendedColumns({ columns, selectYM, downloadOptions });
    const { fileFormat, duration } = value;

    // 若By Platform、By Region、By Country有任意勾选一个，则为dimensionBreakdown 为true
    const dimensionBreakdown = includePlatform || includeRegion || includeCountry;

    // 根据勾选状态动态构建dimensions数组
    const params: Record<string, any> = {
      ...(exportFetchParams || {}),
      page: 0,
      sort: [],
    };
    if (dimensionBreakdown) {
      params.dimensions = [
        includePlatform ? 'platform' : undefined,
        includeRegion ? 'region' : undefined,
        includeCountry ? 'country' : undefined,
      ].filter(Boolean) as string[];
    }
    setLoadingConfirm(true);
    try {
      const response: any = await https({
        url: isAggregate
          ? '/api/v1/globalpedia_pc/studio_monthly_report/mgmt/live_title/summary'
          : '/api/v1/globalpedia_pc/studio_monthly_report/mgmt/live_title/timeline',
        method: 'post',
        data: params,
        rawData: true,
      });

      const fullList = response?.list || response?.data?.list || [];

      let excelDataSource = [];
      if (dimensionBreakdown) {
        excelDataSource = isAggregate ? flattenMetricsBreakdown(fullList) : flattenTimelineDataBreakdown(fullList);
      } else {
        excelDataSource = isAggregate ? flattenMetrics(fullList) : flattenTimelineData(fullList);
      }

      exportData({
        fileName: durationTitle,
        columns: excelColumns,
        data: excelDataSource,
        fileType: fileFormat,
      });
      close?.();
    } catch (error) {
      console.error('Live title export failed:', error);
    } finally {
      setLoadingConfirm(false);
    }
  };

  const duration = useMemo(() => {
    const isSingle = !selectedTitle?.length || selectedTitle.length > 1;
    const [start, end] = [moment(selectedDate[0]).format('YYYY-MM'), moment(selectedDate[1]).format('YYYY-MM')];
    return isSingle ? `${start} ~ ${end}` : `${selectedTitle?.[0] || ''} ${start} ~ ${end}`;
  }, [selectedDate, selectedTitle]);

  // 动态生成 config，根据 loadingConfirm 禁用 Dimensions 和 File Format
  const formConfig = useMemo(
    () => [
      {
        name: 'duration',
        label: 'Duration',
        component: <Input disabled className="gp-live-title-card-filter-download-container-input" />,
        rules: { message: '', required: false },
      },
      {
        name: 'includeDimensions',
        label: 'Dimensions',
        component: (
          <div>
            <Checkbox
              value={includePlatform}
              onChange={(val: boolean) => setIncludePlatform(val)}
              disabled={loadingConfirm}
            >
              By Platform
            </Checkbox>
            <Checkbox
              value={includeRegion}
              onChange={(val: boolean) => setIncludeRegion(val)}
              disabled={loadingConfirm}
            >
              By Region
            </Checkbox>
            <Checkbox
              value={includeCountry}
              onChange={(val: boolean) => setIncludeCountry(val)}
              disabled={loadingConfirm}
            >
              By Country
            </Checkbox>
          </div>
        ),
        rules: { message: '', required: false },
      },
      {
        name: 'fileFormat',
        label: 'File Format',
        component: (
          <Radio.Group disabled={loadingConfirm}>
            <Radio className="dbg-common-radio" name={FILE_TYPES_MAP.EXCEL}>
              Excel
            </Radio>
            <Radio className="dbg-common-radio" name={FILE_TYPES_MAP.CSV}>
              CSV
            </Radio>
          </Radio.Group>
        ),
        rules: { message: '', required: false },
      },
    ],
    [includePlatform, includeRegion, includeCountry, loadingConfirm],
  );

  return (
    <GeneralModal
      title={'Data Analytics Download'}
      className="dbs-live-download-modal"
      modalSize="800"
      isFont14={true}
      isFooter={false}
      visible={visible}
      onClose={close}
      loadingConfirm={loadingConfirm}
      content={
        <div className="gp-live-title-card-filter-download-container font14">
          <UseFormSearch
            config={formConfig}
            getFormValue={(value: any) => getFormValue(value as { fileFormat: string; duration: string })}
            labelSpan={6}
            reset={visible}
            initialValues={{
              duration,
              fileFormat: '1',
            }}
            btnCancelText={'Cancel'}
            isDisabledSearchBtn={true}
            btnLoading={loadingConfirm}
            btnCancelDisabled={loadingConfirm}
            resetCallback={() => {
              close?.();
            }}
          />
        </div>
      }
    />
  );
};
